'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import Header from '@/components/Header'
import HeroParallax from '@/components/HeroParallax'
import Footer from '@/components/Footer'
import { getExperienceById, experiences } from '@/lib/data'
import BookNowCTA from '@/components/BookNowCTA'
import wineStyles from '@/styles/experience-page.module.css'
import styles from '@/styles/experience-page-option3.module.css'
import { BreadcrumbSchema } from '@/lib/schema'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import '@/components/Adventures.css'

interface GalleryItem {
  src: string
  alt: string
  title: string
  description: string
  details: string
}

// Map experience ID (kebab-case) → i18n namespace key (camelCase)
const idToI18nKey: Record<string, string> = {
  'laparva-signature': 'laparvaSignature',
  'wine-route': 'wineRoute',
  'catamaran-trip': 'catamaranTrip',
  'private-dinners-santiago': 'privateDinners',
  'mountain-services': 'mountainServices',
  'andes-lodge': 'andesLodge',
  'mountain-extras': 'mountainExtras',
}

const gallerySrcs = [
  '/images/experiences/catamaran/catamaran-gallery-01.webp',
  '/images/experiences/catamaran/catamaran-gallery-02.webp',
  '/images/experiences/catamaran/catamaran-gallery-03.webp',
  '/images/experiences/catamaran/catamaran-gallery-04.webp',
  '/images/experiences/catamaran/catamaran-gallery-05.webp',
  '/images/experiences/catamaran/catamaran-gallery-06.webp',
]

export default function CatamaranTripPage() {
  const thisExperience = getExperienceById('catamaran-trip')
  const t = useTranslations('experiences')
  const tCommon = useTranslations('common')
  const params = useParams()
  const locale = params.locale as string

  const relatedExperiences = experiences.filter(e => e.id !== 'catamaran-trip' && !e.hidden).slice(0, 2)

  const galleryItems: GalleryItem[] = gallerySrcs.map((src, idx) => ({
    src,
    alt: t(`catamaranTrip.gallery.item${idx + 1}.alt`),
    title: t(`catamaranTrip.gallery.item${idx + 1}.title`),
    description: t(`catamaranTrip.gallery.item${idx + 1}.description`),
    details: t(`catamaranTrip.gallery.item${idx + 1}.details`),
  }))

  const [modalItem, setModalItem] = useState<GalleryItem | null>(null)

  const mainRef = useScrollAnimation<HTMLElement>({
    threshold: 0.12,
    rootMargin: '0px 0px -60px 0px',
  })

  const galleryCarouselRef = useRef<HTMLDivElement | null>(null)
  const [galleryRevealed, setGalleryRevealed] = useState(false)

  useEffect(() => {
    const node = galleryCarouselRef.current
    if (!node || galleryRevealed) return
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setGalleryRevealed(true)
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [galleryRevealed])

  const openModal = useCallback((item: GalleryItem) => {
    setModalItem(item)
    document.body.style.overflow = 'hidden'
  }, [])

  const closeModal = useCallback(() => {
    setModalItem(null)
    document.body.style.overflow = ''
  }, [])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal()
    }
    if (modalItem) {
      window.addEventListener('keydown', handleEsc)
    }
    return () => {
      window.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = ''
    }
  }, [modalItem, closeModal])

  if (!thisExperience) {
    return <div>{t('common.experienceNotFound')}</div>
  }

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: t('index.title'), href: `/${locale}/experiences/` },
        { name: t('catamaranTrip.title'), href: `/${locale}/experiences/catamaran-trip/` },
      ]} />
      <Header />

      <HeroParallax
        backgroundImage={thisExperience.heroImage}
        title={t('catamaranTrip.title')}
        subtitle={t('catamaranTrip.subtitle')}
        location={t('catamaranTrip.location')}
      />

      <main className={wineStyles.pageWrapper} ref={mainRef}>
        {/* Section 1 — 3-column layout from wine-route: Highlights | Feature Image | What's Included */}
        <section className={wineStyles.experienceDetails}>
          <div className="container">
            <div className={wineStyles.detailsGrid}>
              {/* Left: Highlights */}
              <div className={wineStyles.highlightsColumn} data-scroll-item>
                <h3>{t('catamaranTrip.highlightsTitle')}</h3>
                <p className={wineStyles.duration}>{t('catamaranTrip.duration')}</p>

                <div className={wineStyles.highlightItem} data-scroll-item style={{ transitionDelay: '120ms' }}>
                  <strong>{t('catamaranTrip.highlight1Title')}</strong>
                  <p>{t('catamaranTrip.highlight1')}</p>
                </div>

                <div className={wineStyles.highlightItem} data-scroll-item style={{ transitionDelay: '200ms' }}>
                  <strong>{t('catamaranTrip.highlight2Title')}</strong>
                  <p>{t('catamaranTrip.highlight2')}</p>
                </div>

                <div className={wineStyles.highlightItem} data-scroll-item style={{ transitionDelay: '280ms' }}>
                  <strong>{t('catamaranTrip.highlight3Title')}</strong>
                  <p>{t('catamaranTrip.highlight3')}</p>
                </div>

                <div className={wineStyles.highlightItem} data-scroll-item style={{ transitionDelay: '360ms' }}>
                  <strong>{t('catamaranTrip.highlight4Title')}</strong>
                  <p>{t('catamaranTrip.highlight4')}</p>
                </div>
              </div>

              {/* Center: Feature Image */}
              <div className={wineStyles.featureColumn} data-scroll-item style={{ transitionDelay: '150ms' }}>
                <div className={wineStyles.featureWrapper}>
                  <img
                    src="/images/experiences/catamaran/catamaran-feature.webp"
                    alt={t('catamaranTrip.alts.featureImage')}
                    className={wineStyles.featureImage}
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Right: What's Included */}
              <div className={wineStyles.includedColumn} data-scroll-item style={{ transitionDelay: '300ms' }}>
                <h3>{t('common.whatsIncluded')}</h3>
                <ul>
                  <li>{t('catamaranTrip.included.item1')}</li>
                  <li>{t('catamaranTrip.included.item2')}</li>
                  <li>{t('catamaranTrip.included.item3')}</li>
                  <li>{t('catamaranTrip.included.item4')}</li>
                  <li>{t('catamaranTrip.included.item5')}</li>
                  <li>{t('catamaranTrip.included.item6')}</li>
                  <li>{t('catamaranTrip.included.item7')}</li>
                  <li>{t('catamaranTrip.included.item8')}</li>
                  <li>{t('catamaranTrip.included.item9')}</li>
                  <li>{t('catamaranTrip.included.item10')}</li>
                </ul>
              </div>
            </div>

            {/* Below 3-col grid: Quick facts + tagline */}
            <div className={wineStyles.detailsBelow}>
              <ul className={wineStyles.quickFacts} data-scroll-item>
                <li>{t('catamaranTrip.quickFacts.fact1')}</li>
                <li>{t('catamaranTrip.quickFacts.fact2')}</li>
                <li>{t('catamaranTrip.quickFacts.fact3')}</li>
                <li>{t('catamaranTrip.quickFacts.fact4')}</li>
                <li>{t('catamaranTrip.quickFacts.fact5')}</li>
              </ul>

              <div className={wineStyles.taglineBlock} data-scroll-item style={{ transitionDelay: '160ms' }}>
                <h3 className={wineStyles.taglineHeadline}>
                  {t('catamaranTrip.tagline.headline')}
                </h3>
                <p className={wineStyles.taglineDescription}>
                  {t('catamaranTrip.tagline.description')}
                </p>
                <p className={wineStyles.taglineIdealFor}>
                  {t('catamaranTrip.tagline.idealFor')}
                </p>
              </div>
            </div>
          </div>
        </section>

        <div data-scroll-item>
          <BookNowCTA
            variant="inline"
            productId="catamaran-trip"
            productType="experience"
            productName={t('catamaranTrip.title')}
            bookingMode={thisExperience.bookingMode || 'whatsapp'}
          />
        </div>

        {/* Section — Weekly itinerary, day by day */}
        <section className={wineStyles.itinerarySection}>
          <div className="container">
            <div className={wineStyles.itineraryHeader} data-scroll-item>
              <h2 className={wineStyles.itineraryTitle}>{t('catamaranTrip.itinerary.sectionTitle')}</h2>
              <p className={wineStyles.itinerarySubtitle}>{t('catamaranTrip.itinerary.sectionSubtitle')}</p>
            </div>

            <div className={wineStyles.itineraryList}>
              {[1, 2, 3, 4, 5, 6, 7].map((n, i) => (
                <div
                  key={n}
                  className={wineStyles.itineraryDay}
                  data-scroll-item
                  style={{ transitionDelay: `${i * 90}ms` }}
                >
                  <span className={wineStyles.itineraryDayLabel}>
                    {t(`catamaranTrip.itinerary.day${n}Label`)}
                  </span>
                  <h3 className={wineStyles.itineraryDayTitle}>
                    {t(`catamaranTrip.itinerary.day${n}Title`)}
                  </h3>
                  <p className={wineStyles.itineraryDayText}>
                    {t(`catamaranTrip.itinerary.day${n}Text`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section — Preparation dinner before the trip */}
        <section className={wineStyles.preparationSection}>
          <div className="container">
            <h2 className={wineStyles.preparationTitleTop} data-scroll-item>
              {t('catamaranTrip.preparation.sectionTitle')}
            </h2>
            <div className={wineStyles.preparationBlock} data-scroll-item style={{ transitionDelay: '140ms' }}>
              <span className={wineStyles.preparationOverline}>
                {t('catamaranTrip.preparation.overline')}
              </span>
              <h3 className={wineStyles.preparationTitle}>
                {t('catamaranTrip.preparation.title')}
              </h3>
              <p className={wineStyles.preparationText}>
                {t('catamaranTrip.preparation.text')}
              </p>
            </div>
          </div>
        </section>

        {/* Section 2 — Gallery carousel w/ modal, laparva-signature style */}
        <section className={styles.gallery}>
          <div className={styles.galleryContainer}>
            <div className={styles.sectionHeader} data-scroll-item>
              <h2 className={styles.sectionTitle}>{t('catamaranTrip.galleryTitle')}</h2>
              <p className={styles.sectionDescription}>
                {t('catamaranTrip.galleryDescription')}
              </p>
            </div>

            <div
              className={styles.galleryCarousel}
              ref={galleryCarouselRef}
              data-revealed={galleryRevealed ? 'true' : 'false'}
            >
              <Swiper
                modules={[Navigation, Pagination]}
                slidesPerView={1}
                spaceBetween={24}
                loop={true}
                grabCursor={true}
                navigation={{
                  nextEl: '.swiper-button-next',
                  prevEl: '.swiper-button-prev',
                }}
                pagination={{
                  el: `.${styles.galleryPagination}`,
                  clickable: true,
                  dynamicBullets: true,
                }}
                breakpoints={{
                  640: { slidesPerView: 2, spaceBetween: 24 },
                  1024: { slidesPerView: 3, spaceBetween: 32 },
                }}
                className={styles.gallerySwiper}
              >
                {galleryItems.map((item, idx) => (
                  <SwiperSlide key={idx}>
                    <article
                      className={styles.galleryCard}
                      onClick={() => openModal(item)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') openModal(item) }}
                    >
                      <div className={styles.galleryCardImage}>
                        <img src={item.src} alt={item.alt} loading="lazy" />
                      </div>
                      <div className={styles.galleryCardOverlay}>
                        <h3 className={styles.galleryCardTitle}>{item.title}</h3>
                        <div className={styles.galleryCardContent}>
                          <p>{item.description}</p>
                          <span className={styles.galleryCardLink}>
                            {t('catamaranTrip.viewDetails')} &rarr;
                          </span>
                        </div>
                      </div>
                    </article>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className="swiper-button-prev"></div>
              <div className="swiper-button-next"></div>
              <div className={styles.galleryPagination}></div>
            </div>

            <div className={styles.sectionCta} data-scroll-item>
              <a href={`/${locale}/experiences/`} className="btn-primary">
                {t('common.exploreExperiences')} &rarr;
              </a>
            </div>
          </div>
        </section>

        {/* Gallery Detail Modal */}
        {modalItem && (
          <div
            className={styles.galleryModal}
            onClick={closeModal}
            role="dialog"
            aria-modal="true"
            aria-label={modalItem.title}
          >
            <div
              className={styles.galleryModalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={styles.galleryModalClose}
                onClick={closeModal}
                aria-label={t('common.closeModal')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>

              <div className={styles.galleryModalImageWrap}>
                <img src={modalItem.src} alt={modalItem.alt} />
              </div>

              <div className={styles.galleryModalBody}>
                <span className={styles.galleryModalOverline}>{t('catamaranTrip.modalOverline')}</span>
                <h3 className={styles.galleryModalTitle}>{modalItem.title}</h3>
                <div className={styles.galleryModalDivider} />
                <p className={styles.galleryModalText}>{modalItem.details}</p>
              </div>
            </div>
          </div>
        )}

        <section className={styles.relatedSection}>
          <div className="container">
            <h2 data-scroll-item>{t('common.relatedExperiences')}</h2>
            <div className={styles.relatedGrid}>
              {relatedExperiences.map((experience, i) => {
                const i18nKey = idToI18nKey[experience.id]
                const localizedTitle = i18nKey ? t(`${i18nKey}.title`) : experience.title
                const localizedSubtitle = i18nKey ? t(`${i18nKey}.subtitle`) : experience.subtitle
                return (
                  <article
                    key={experience.id}
                    className={styles.relatedCard}
                    data-scroll-item
                    style={{ transitionDelay: `${120 + i * 120}ms` }}
                  >
                    <div className={styles.relatedImage}>
                      <img src={experience.heroImage} alt={localizedTitle} loading="lazy" />
                    </div>
                    <div className={styles.relatedOverlay}>
                      <h3 className={styles.relatedTitle}>{localizedTitle}</h3>
                      <div className={styles.relatedContent}>
                        <p>{localizedSubtitle}</p>
                        <a href={`/${locale}/experiences/${experience.id}/`} className={styles.relatedLink}>
                          {t('common.explore')} &rarr;
                        </a>
                      </div>
                    </div>
                  </article>
                )
              })}
            </div>
          </div>
        </section>

        <div data-scroll-item>
          <BookNowCTA
            variant="closing"
            productId="catamaran-trip"
            productType="experience"
            productName={t('catamaranTrip.title')}
            bookingMode={thisExperience.bookingMode || 'whatsapp'}
          />
        </div>
      </main>

      <Footer />
    </>
  )
}
